import {
  isPatientEmailValid,
  isPatientMobileValid,
} from 'src/clinic/components/ClinicManageBookingsPage/newBookingCalendarUtils'
import { isValidEmail } from 'src/clinic/views/onboardng/clinicBrand/components/utils'
import { removePlusFromBeginning } from 'src/consumer/helpers/utilFunctions'
import {
  getPatientAgeValidationMessage,
  hasValidPatientAgeInput,
} from './patientAgeUtils'

export const validateRequiredField = (value) => {
  return Boolean(value && value.toString().trim().length > 0)
}

export const validateName = (patientDetails) => {
  return validateRequiredField(patientDetails?.name)
}

export const validateGender = (patientDetails) => {
  return validateRequiredField(patientDetails?.gender)
}

export const validateAge = (patientDetails) => {
  return hasValidPatientAgeInput(patientDetails)
}

export const validateMobile = (patientDetails, makeAnonymousPatient) => {
  return isPatientMobileValid(patientDetails, makeAnonymousPatient)
}

export const validateEmail = (patientDetails, makeAnonymousPatient) => {
  return isPatientEmailValid(patientDetails, makeAnonymousPatient)
}

export const validateAttendantName = (attendant) => {
  return validateRequiredField(attendant?.name)
}

export const validateAlternativeMobile = (mobile) => {
  if (!mobile || mobile.trim() === '') return true // Optional field
  return mobile.length >= 10
}

export const validateAlternativeEmail = (email) => {
  if (!email || email.trim() === '') return true
  return isValidEmail(email.trim())
}

export const createPatientInputValidations = (patientDetails, makeAnonymousPatient, hideInputValidations) => {
  if (hideInputValidations) return {}

  return {
    name: validateName(patientDetails),
    gender: validateGender(patientDetails),
    age: validateAge(patientDetails),
    mobile: validateMobile(patientDetails, makeAnonymousPatient),
    email: validateEmail(patientDetails, makeAnonymousPatient),
  }
}

export const getAllValidationErrors = (patientDetails, makeAnonymousPatient) => {
  const errors = []
  const validations = createPatientInputValidations(patientDetails, makeAnonymousPatient, false)

  Object.entries(validations).forEach(([field, isValid]) => {
    if (!isValid) {
      errors.push({
        field,
        message: getValidationMessage(field)
      })
    }
  })

  return errors
}

export const getValidationMessage = (fieldName, patientDetails) => {
  const messages = {
    name: 'Name is required',
    gender: 'Please choose a gender',
    age: getPatientAgeValidationMessage(patientDetails) || 'Valid age is required',
    mobile: 'Valid mobile number is required',
    email: 'Valid email is required',
  }

  return messages[fieldName] || 'Field is required'
}

export const isFormValid = (patientDetails, makeAnonymousPatient) => {
  const validations = createPatientInputValidations(patientDetails, makeAnonymousPatient, false)
  return Object.values(validations).every(isValid => isValid)
}

export const isMakingRelatedPatientValid = (patientDetails, loadedPatientOptions, addRelatedPatient, inputValue) => {
  const patientMobile = inputValue || patientDetails?.mobile

  if (!addRelatedPatient) {
    return false
  }

  const isMobileComplete = patientMobile && patientMobile.length >= 10
  const patientsWithSameMobile = loadedPatientOptions?.filter(
    (option) => removePlusFromBeginning(option.mobile) === removePlusFromBeginning(patientMobile),
  )

  return isMobileComplete && patientsWithSameMobile?.length > 0
}

export const findPrimaryPatientForRelated = (patientDetails, loadedPatientOptions) => {
  const patientsWithSameMobile = loadedPatientOptions?.filter(
    (option) =>
      removePlusFromBeginning(option.mobile) === removePlusFromBeginning(patientDetails?.mobile),
  )

  return patientsWithSameMobile.find(
    (patient) => !patient?.primaryPatientProfile?.patientId,
  )
}

// A row with no name is silently dropped on save (see cleanAttendantsArray), so the only
// attendant error worth surfacing is "you typed a contact but no name" — that would lose data.
export const validateAttendants = (attendants) => {
  if (!attendants || attendants.length === 0) return []

  return attendants
    .map((attendant, index) => {
      const hasContact =
        validateRequiredField(attendant?.mobile) || validateRequiredField(attendant?.email)
      const isValid = validateAttendantName(attendant) || !hasContact
      return {
        index,
        isValid,
        message: isValid ? null : 'Attendant name is required',
      }
    })
    .filter((validation) => !validation.isValid)
}

export const validateAlternativeMobiles = (altMobiles) => {
  if (!altMobiles || altMobiles.length === 0) return []

  return altMobiles.map((mobile, index) => ({
    index,
    isValid: validateAlternativeMobile(mobile),
    message: validateAlternativeMobile(mobile) ? null : 'Invalid mobile number format'
  })).filter(validation => !validation.isValid)
}

export const validateAlternativeEmails = (altEmails) => {
  if (!altEmails || altEmails.length === 0) return []

  return altEmails.map((email, index) => ({
    index,
    isValid: validateAlternativeEmail(email),
    message: validateAlternativeEmail(email) ? null : 'Invalid email address',
  })).filter(validation => !validation.isValid)
}

export const shouldShowValidationError = (isValid, hideInputValidations) => {
  return !isValid && !hideInputValidations
}

/**
 * Human-readable list of what still blocks patient creation, in form order. `labels` comes from
 * the i18n copy so the hint reads in the assistant's chosen form language.
 */
export const getMissingRequiredPatientFields = (
  patientDetails,
  makeAnonymousPatient,
  labels = {},
  { requireConsent = false } = {},
) => {
  const missing = []
  if (!makeAnonymousPatient && !(
    validateMobile(patientDetails, makeAnonymousPatient) &&
    validateEmail(patientDetails, makeAnonymousPatient)
  )) {
    missing.push(labels.mobileOrEmail || 'Mobile or email')
  }
  if (!validateName(patientDetails)) missing.push(labels.name || 'Name')
  if (!validateGender(patientDetails)) missing.push(labels.gender || 'Gender')
  if (!validateAge(patientDetails)) missing.push(labels.age || 'Age')
  if (requireConsent && !patientDetails?.consentDraft?.accepted) {
    missing.push(labels.consent || 'Consent')
  }
  return missing
}
